import React, { useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  CircularProgress, 
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Grid
} from '@mui/material';
import { 
  Add as AddIcon,
  Person,
  ArrowForward,
  Assignment,
  Schedule
} from '@mui/icons-material';
import DriverList from './DriverList';
import DriverForm from './DriverForm';
import { useNavigate } from 'react-router-dom';

const DriversPage = () => {
  const [open, setOpen] = useState(false);
  const [editDriver, setEditDriver] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  const handleAdd = () => {
    setEditDriver(null);
    setIsEditing(true);
    setOpen(true);
  };

  const handleEdit = (driver) => {
    setEditDriver(driver);
    setIsEditing(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditDriver(null);
    setIsEditing(false);
  };

  const handleFormClose = (shouldRefetch) => {
    handleClose();
    if (shouldRefetch) {
      // Trigger a refetch of the data instead of full page reload
      // The parent component should handle the refetch
    }
  };

  return (
    <Box sx={{ 
      p: 4, 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', 
      minHeight: '100vh' 
    }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            sx={{ 
              bgcolor: '#8b5cf6', 
              width: 56, 
              height: 56, 
              mr: 2,
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)'
            }}
          >
            <Person sx={{ fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800, 
                background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5
              }}
            >
              Fleet Drivers
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              Manage your driver team and assignments
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Action Cards */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(139, 92, 246, 0.2)',
              }
            }} onClick={handleAdd}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar 
                  sx={{ 
                    bgcolor: '#8b5cf6', 
                    width: 64, 
                    height: 64, 
                    mx: 'auto', 
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)'
                  }}
                >
                  <AddIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Add New Driver
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Register a new driver to your fleet team
                </Typography>
                <Chip 
                  label="Quick Add" 
                  color="secondary" 
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </CardContent>
            </Card>
          </Grid>
          {/* <Grid item xs={12} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(99, 102, 241, 0.2)',
              }
            }} onClick={() => navigate('/trips')}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar 
                  sx={{ 
                    bgcolor: '#6366f1', 
                    width: 64, 
                    height: 64, 
                    mx: 'auto', 
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)'
                  }}
                >
                  <ArrowForward sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 6, mb: 1 }}>
                  Trip Management
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  View and manage driver trip assignments
                </Typography>
                <Chip 
                  label="View All" 
                  color="primary" 
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </CardContent>
            </Card>
          </Grid> */}
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.2)',
              }
            }} onClick={() => navigate('/fuel-logs')}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar 
                  sx={{ 
                    bgcolor: '#10b981', 
                    width: 64, 
                    height: 64, 
                    mx: 'auto', 
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                  }}
                >
                  <Schedule sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Fuel Logs
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Track driver fuel consumption and costs
                </Typography>
                <Chip 
                  label="Manage" 
                  color="success" 
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Driver List */}
      <Card sx={{ 
        borderRadius: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
            Driver Team
          </Typography>
      <DriverList onEdit={handleEdit} />
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editDriver ? (isEditing ? 'Edit Driver' : 'View Driver') : 'Add Driver'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <DriverForm 
            driver={editDriver} 
            onClose={handleFormClose}
            isEditing={isEditing}
            onToggleEdit={() => setIsEditing(!isEditing)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DriversPage; 